import { useCallback, useEffect, useState } from 'react';

import {
  LEADS_PAGE_PATH,
  SALES_FUNNELS_LEADS_PATH,
} from '@app/routes/pages/leadsPages/constants';

// Пункт «Лиды» в меню один и ведёт в тот раздел, где пользователь был
// последним. Ключ — по образцу соседнего sidebar:open-keys: раскладка меню
// одинакова для всех филиалов, отдельный ключ на филиал был бы лишним.
const STORAGE_KEY = 'sidebar:leads-section';

const LEADS_SECTIONS = ['registry', 'funnels'] as const;

type LeadsSection = (typeof LEADS_SECTIONS)[number];

const DEFAULT_SECTION: LeadsSection = 'registry';

const LEADS_SECTION_PATHS: Record<LeadsSection, string> = {
  registry: LEADS_PAGE_PATH,
  funnels: SALES_FUNNELS_LEADS_PATH,
};

const isLeadsSection = (value: unknown): value is LeadsSection =>
  LEADS_SECTIONS.some((section) => section === value);

// Недоступное или засорённое хранилище означает обычный старт с реестра.
const readStoredSection = (): LeadsSection => {
  try {
    const rawValue = window.localStorage.getItem(STORAGE_KEY);

    return isLeadsSection(rawValue) ? rawValue : DEFAULT_SECTION;
  } catch {
    return DEFAULT_SECTION;
  }
};

// Раздел определяем по адресу, а не по клику: переключатель «Реестр / Воронки»
// в шапке страницы меняет его в обход меню, и запоминать надо оба пути.
const resolveSectionByPath = (pathname: string): LeadsSection | null => {
  if (pathname.startsWith(SALES_FUNNELS_LEADS_PATH)) return 'funnels';
  if (pathname.startsWith(LEADS_PAGE_PATH)) return 'registry';

  return null;
};

interface UseLeadsSectionMemoryResult {
  leadsSectionPath: string;
}

export const useLeadsSectionMemory = (
  pathname: string
): UseLeadsSectionMemoryResult => {
  const [section, setSection] = useState<LeadsSection>(readStoredSection);

  const rememberSection = useCallback((nextSection: LeadsSection) => {
    setSection(nextSection);

    try {
      window.localStorage.setItem(STORAGE_KEY, nextSection);
    } catch {
      // Приватный режим или заблокированное хранилище: в этой сессии пункт
      // просто продолжит вести в раздел, открытый последним.
    }
  }, []);

  useEffect(() => {
    const visitedSection = resolveSectionByPath(pathname);
    if (!visitedSection || visitedSection === section) return;

    rememberSection(visitedSection);
  }, [pathname, rememberSection, section]);

  return { leadsSectionPath: LEADS_SECTION_PATHS[section] };
};
